/**
 * The addresses of the Sessions screens (§12, §15), spelled ONCE for every
 * reader that points at them: the Matière tab of the frontend and the read
 * tools of the MCP assistant (§20.5, issue #94).
 *
 * ## The one thing worth a module: `?tour=` is a NUMBER
 *
 * The conversation of §12.3 pins a turn by the number a human reads beside the
 * reply - « Tour 4 » - so `?tour=` is 1-based (`readPinnedTurn` in the
 * frontend refuses anything under 1), while a turn's `index` is 0-based across
 * the session (§8.7). Two builders had spelled that address, one with the
 * shift and one without, and the one without pinned the wrong turn on every
 * extract of the Matière tab. The shift lives here, and nowhere else.
 *
 * ## And the second: `?couches=` has an ORDER
 *
 * The open layers of the conversation travel in the URL too (§12.3), and the
 * screen writes them back in the order of its bar, whatever was pressed first,
 * so two readers who opened the same layers hold the same link. A tool that
 * hands out such a link writes the same order, from the same list.
 */

export function sessionUrl(sessionId: string): string {
  return `/admin/sessions/${encodeURIComponent(sessionId)}`;
}

/**
 * The six layers of the conversation (§12.3), as `?couches=` spells them and in
 * the order the bar lists them - `etat` last and apart, the sticky column after
 * the five accordions. The frontend's `SESSION_LAYERS` is this list, and the
 * MCP read tools translate their English names into it.
 */
export const CONVERSATION_LAYERS = [
  'moteur',
  'objectifs',
  'gardefous',
  'appels',
  'brut',
  'etat',
] as const;
export type ConversationLayer = (typeof CONVERSATION_LAYERS)[number];

/**
 * The conversation of a session, pinned on a turn by its 0-based `index` when
 * one is named, with the layers to open written after it - in the bar's order,
 * never in the order they were asked in - when some are.
 */
export function conversationUrl(
  sessionId: string,
  turnIndex?: number,
  openLayers: Iterable<ConversationLayer> = [],
): string {
  const open = new Set(openLayers);
  const params: string[] = [];
  if (turnIndex !== undefined) params.push(`tour=${turnIndex + 1}`);
  if (open.size > 0) {
    params.push(`couches=${CONVERSATION_LAYERS.filter((layer) => open.has(layer)).join(',')}`);
  }
  const base = `${sessionUrl(sessionId)}/protocole`;
  return params.length === 0 ? base : `${base}?${params.join('&')}`;
}

/** The Matière tab of §12.4, narrowed to one experience. */
export function matterUrl(experienceId: string): string {
  return `/admin/sessions?tab=matiere&experience=${encodeURIComponent(experienceId)}`;
}

/** The transcript of a blind interview (S10 of the chat spec). */
export function interviewUrl(roomId: string): string {
  return `/admin/entretiens/${encodeURIComponent(roomId)}`;
}
